import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import SimpleTooltip from '@panel/commons/lib/Tooltip/SimpleTooltip';
import { useTranslation } from 'react-i18next';
import {
  SelectContainer,
  SelectLabel,
  SelectHeader,
  SelectPlaceholder,
  SelectValue,
  CloseIconWrapper,
  SelectContentWrapper,
  ArrowIcon,
  OptionsWrapper,
  OptionItem,
  Description,
  FilterInput,
  NoResultsMessage
} from './styles.js';
import CheckIcon from '../../OptionButton/CheckIcon.js';
import CloseIcon from './CloseIcon.js';
import useFilterOptions from '../../../hooks/useFilterOptions.js';

const FormSelect = ({
  label,
  options,
  value,
  onChange,
  placeholder = '',
  filterPlaceholder = '',
  device,
  hasError,
  isDisabled,
  filterable = false,
  noOptionToClean = false
}) => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const labelRef = useRef(null);
  const filterInputRef = useRef(null);
  const optionRefs = useRef([]);

  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(options.find(option => option.value === value) || null);
  const [showPosition, setShowPosition] = useState(false);
  const [hasEllipsis, setHasEllipsis] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const { filterText, filteredOptions, showNoResults, handleFilterChange, clearFilter } = useFilterOptions(options);

  const displayOptions = filterable ? filteredOptions : options;

  // Función para detectar si el texto tiene ellipsis
  const checkForEllipsis = () => {
    if (labelRef.current) {
      const element = labelRef.current;
      const hasOverflow = element.offsetWidth < element.scrollWidth;
      setHasEllipsis(hasOverflow);
    }
  };

  useEffect(() => {
    checkForEllipsis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption?.description]);

  useLayoutEffect(() => {
    // Efecto para posicionar 'hacia arriba' o 'hacia abajo' al dropdown:
    const viewportHeight = window.innerHeight;
    const changePosition = dropdownRef.current?.getBoundingClientRect().bottom > viewportHeight;
    setShowPosition(changePosition);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    setSelectedOption(options.find(option => option.value === value) || null);
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        clearFilter();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Focus en el input de filtro cuando se abre el dropdown sin opción seleccionada
  useEffect(() => {
    if (isOpen && filterable && !selectedOption && filterInputRef.current) {
      filterInputRef.current.focus();
    }
  }, [isOpen, filterable, selectedOption]);

  // Reseteamos el highlightedIndex cuando cambian las opciones filtradas o se cierra el dropdown
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [filteredOptions, isOpen]);

  // Scroll al elemento destacado para visualizarlo cuando hay muchas opciones y evitamos que se salga del área visible
  useEffect(() => {
    if (highlightedIndex >= 0 && optionRefs.current[highlightedIndex]) {
      optionRefs.current[highlightedIndex].scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [highlightedIndex]);

  const handleSelectedOption = option => {
    setSelectedOption(option);
    onChange(option.value);
    setIsOpen(false);
    clearFilter();
  };

  const handleClearSelection = e => {
    e.stopPropagation();
    setSelectedOption(null);
    onChange('');
    clearFilter();
  };

  const toggleDropdown = () => {
    if (!isOpen) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
      clearFilter();
    }
  };

  const onFilterInputChange = e => {
    handleFilterChange(e);
    if (!isOpen) setIsOpen(true);
  };

  const onFilterInputFocus = () => {
    if (!isOpen) setIsOpen(true);
  };

  const onFilterInputClick = e => {
    // Evitamos que el click propague al SelectHeader (que haría toggle)
    e.stopPropagation();
  };

  const handleKeyDown = e => {
    if (!isOpen || showNoResults || displayOptions.length === 0) return;

    if (e.key === 'ArrowDown') {
      setHighlightedIndex(prevIndex => {
        return prevIndex < displayOptions.length - 1 ? prevIndex + 1 : prevIndex;
      });
    } else if (e.key === 'ArrowUp') {
      setHighlightedIndex(prevIndex => {
        return prevIndex > 0 ? prevIndex - 1 : 0;
      });
    } else if (e.key === 'Enter') {
      if (highlightedIndex >= 0 && highlightedIndex < displayOptions.length) {
        handleSelectedOption(displayOptions[highlightedIndex]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      clearFilter();
    }
  };

  const renderSelectedValue = () => {
    if (!selectedOption) return null;

    return (
      <>
        {hasEllipsis ? (
          <SimpleTooltip
            isMobile={device === 'tablet' || device === 'mobile'}
            width='154px'
            height='fit-content'
            position='bottom-right'
            text={selectedOption.description}
            dataPanel={`tooltip-${selectedOption.description}`}
            backgroundColor='#3c3834'
          >
            <SelectValue ref={labelRef} device={device} data-publi={`selected-value-${label}`}>
              {selectedOption.description}
            </SelectValue>
          </SimpleTooltip>
        ) : (
          <SelectValue ref={labelRef} device={device} data-publi={`selected-value-${label}`}>
            {selectedOption.description}
          </SelectValue>
        )}
        {!isDisabled && !noOptionToClean && (
          <CloseIconWrapper data-publi={`close-icon-${label}`} onClick={handleClearSelection}>
            <CloseIcon />
          </CloseIconWrapper>
        )}
      </>
    );
  };

  const renderHeaderContent = () => {
    if (selectedOption) {
      return renderSelectedValue();
    }

    // Sin opción seleccionada con filtro activo: mostramos el input
    if (filterable) {
      return (
        <FilterInput
          ref={filterInputRef}
          type='text'
          value={filterText}
          isDisabled={isDisabled}
          disabled={isDisabled}
          onChange={onFilterInputChange}
          onFocus={onFilterInputFocus}
          onClick={onFilterInputClick}
          onKeyDown={handleKeyDown}
          placeholder={filterPlaceholder}
        />
      );
    }

    // Sin opción seleccionada: mostramos placeholder
    return <SelectPlaceholder isDisabled={isDisabled}>{placeholder}</SelectPlaceholder>;
  };

  return (
    <SelectContainer ref={containerRef}>
      {label && (
        <SelectLabel data-publi='select-categories-label' disabled={isDisabled}>
          {label}
        </SelectLabel>
      )}
      <SelectHeader
        data-publi='select-categories-header'
        onClick={isDisabled ? () => {} : toggleDropdown}
        isOpen={isOpen}
        error={hasError}
        disabled={isDisabled}
      >
        <SelectContentWrapper>{renderHeaderContent()}</SelectContentWrapper>
        <ArrowIcon isDisabled={isDisabled} device={device} isOpen={isOpen} />
      </SelectHeader>

      <OptionsWrapper
        data-publi='select-categories-options'
        ref={dropdownRef}
        isOpen={isOpen}
        openToUp={showPosition}
        maxHeight={device === 'mobile' ? '273px' : '390px'}
      >
        {showNoResults ? (
          <NoResultsMessage>{t('professionalPubli.location.formSelect.noFilterResults')}</NoResultsMessage>
        ) : (
          displayOptions.map((option, index) => (
            <React.Fragment key={option.value}>
              <OptionItem
                ref={el => {
                  optionRefs.current[index] = el;
                }}
                data-publi={`select-categorie-${option.value}`}
                selected={selectedOption?.value === option.value}
                onClick={() => handleSelectedOption(option)}
                isHighlighted={highlightedIndex === index}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <Description selected={selectedOption?.value === option.value}>{option.description}</Description>
                {selectedOption?.value === option.value && <CheckIcon />}
              </OptionItem>
            </React.Fragment>
          ))
        )}
      </OptionsWrapper>
    </SelectContainer>
  );
};

export default FormSelect;
